import { GroupedTable } from "@/components/GroupedTable/GroupedTable";
import { Page, PageBody, PageHeader } from "@/components/app-layout";
import { UserDisplayName } from "@/components/display/UserDisplayName";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Avatar } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Column, Row } from "@/components/ui/flex";
import { TableSkeleton } from "@/components/ui/skeleton";
import { LinkUserToGroupDialog } from "@/components/user/LinkUserToGroupDialog";
import { GroupRoleSelection } from "@/components/user/RoleSelection";
import { useGroupData } from "@/data/use-groups-data";
import { GroupUser, useUsersData } from "@/data/use-users-data";
import { GroupRole } from "@/lib/consts";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEye,
  faUnlink,
} from "@nnr/fontawesome-sharp-light/icons/sharp/light";
import { AvatarFallback } from "@radix-ui/react-avatar";
import { Link, useParams } from "@tanstack/react-router";
import { toast } from "sonner";

export default function GlobalAdminGroupUsersPage() {
  const { groupUuid } = useParams({ strict: false }) as {
    groupUuid?: string;
  };
  const { group } = useGroupData(groupUuid);
  const {
    users,
    isLoading: usersLoading,
    unlinkUser,
    updateUserRole,
  } = useUsersData(groupUuid ?? null);

  const handleUnlink = async (user: GroupUser) => {
    await unlinkUser(user.uuid);
  };

  if (!group) return null;

  const isPersonalGroup = group.personal;

  return (
    <Page>
      <PageHeader>
        <PageHeader.Title>Users</PageHeader.Title>
      </PageHeader>
      <PageBody innerClassName="gap-8">
        {isPersonalGroup && (
          <p>
            This is a personal group. Membership is fixed to the owner. The
            owner&apos;s role can be Member or Creator.
          </p>
        )}
        <Row className="items-center justify-between">
          <h2 className="text-xl font-bold">Users</h2>
          {!isPersonalGroup && <LinkUserToGroupDialog groupUuid={groupUuid} />}
        </Row>
        <div className="space-y-2">
          {usersLoading && <TableSkeleton />}
          {!usersLoading && (
            <GroupedTable
              data={[
                {
                  groupId: "users",
                  label: "Users",
                  items: users,
                },
              ]}
              columns={[
                {
                  key: "name",
                  label: "Name",
                },
                {
                  key: "email",
                  label: "Email",
                },
                {
                  key: "groupRole",
                  label: "Group Role",
                },
              ]}
              renderCell={{
                name: (_, row) => <UserDisplayName user={row} />,
                groupRole: (value, row) => (
                  <GroupRoleSelection
                    value={value as GroupRole}
                    disableRoleOption={isPersonalGroup ? ["admin"] : undefined}
                    onChange={(role) =>
                      updateUserRole(
                        { userUuid: row.uuid, role },
                        {
                          onSuccess: (data) => {
                            if (data.response.ok) {
                              toast.success("User role updated successfully");
                            } else {
                              toast.error("Failed to update user role");
                            }
                          },
                          onError: () => {
                            toast.error("Failed to update user role");
                          },
                        },
                      )
                    }
                  />
                ),
              }}
              renderActions={({ row }) => (
                <Row className="items-center gap-2">
                  <Link
                    to="/admin/users/$userUuid"
                    params={{ userUuid: row.uuid }}
                  >
                    <FontAwesomeIcon icon={faEye} />
                  </Link>

                  {!isPersonalGroup && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="hover:bg-destructive hover:text-destructive-foreground"
                          title="Remove"
                        >
                          <FontAwesomeIcon icon={faUnlink} />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Unlink User?</AlertDialogTitle>
                          <AlertDialogDescription>
                            <Column className="gap-2">
                              <div className="relative flex gap-2 self-center p-4">
                                <UserDisplayName user={row} variant="large" />
                                <Avatar className="bg-destructive absolute -top-1 -right-1 h-12 w-12 text-white">
                                  <AvatarFallback>
                                    <FontAwesomeIcon icon={faUnlink} />
                                  </AvatarFallback>
                                </Avatar>
                              </div>
                              <div>
                                Are you sure you want to unlink {row.name}{" "}
                                {row.email} from the group?
                              </div>
                            </Column>
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            onClick={() => handleUnlink(row)}
                            asChild
                          >
                            <Button size="sm">Unlink</Button>
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                </Row>
              )}
            />
          )}
        </div>
      </PageBody>
    </Page>
  );
}
